import React, { useCallback, useEffect, useState } from 'react';

import MenuItem from '@mui/material/MenuItem';
import QuestionMark from '@mui/icons-material/QuestionMark';
import MenuList from '@mui/material/MenuList';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import ServerInfoDialog from '../Dialogs/Help/ServerInfoDialog';
import Box from '@mui/material/Box';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import Tooltip from '@mui/material/Tooltip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

interface Props {
  isSideBarOpen?: boolean;
}

const KeyStyled = ({
  content,
  ignoreMarginRight = false,
}: {
  content: string;
  ignoreMarginRight?: boolean;
}) => (
  <Box
    display="inline-block"
    py="0.2em"
    px="0.5em"
    mr={ignoreMarginRight ? undefined : '1em'}
    borderRadius="3px"
    border="1px solid"
  >
    {content}
  </Box>
);

const LinkStyle: React.CSSProperties = { color: 'inherit', textDecoration: 'none' };

export default function NavBarHelpMenu({ isSideBarOpen = false }: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [isRootHovered, setIsRootHovered] = useState(false);
  const [isRootPaperHovered, setIsRootPaperHovered] = useState(false);
  const [showShortcutsDialog, setShowShortcutsDialog] = useState(false);
  const [showServerInfoDialog, setShowServerInfoDialog] = useState(false);

  const handleOpenMenu = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setIsRootHovered(true);
    setAnchor(e.currentTarget);
  }, []);

  const handleCloseMenu = useCallback(() => setAnchor(null), []);

  useEffect(() => {
    if (Boolean(anchor) && !isRootHovered && !isRootPaperHovered) setAnchor(null);
  }, [anchor, isRootHovered, isRootPaperHovered]);

  const handleOpenShortcutsDialog = useCallback(() => {
    setShowShortcutsDialog(true);
    setAnchor(null);
  }, []);

  const handleOpenServerInfoDialog = useCallback(() => {
    setShowServerInfoDialog(true);
    setAnchor(null);
  }, []);

  return (
    <>
      <ListItem
        disablePadding
        sx={{ display: 'block', marginTop: '6px' }}
        onMouseEnter={handleOpenMenu}
        onMouseLeave={() => setIsRootHovered(false)}
      >
        <Tooltip
          title="Help"
          placement="right"
          disableHoverListener={isSideBarOpen}
          slotProps={{
            popper: { modifiers: [{ name: 'offset', options: { offset: [-335, 0] } }] },
          }}
        >
          <ListItemButton
            sx={{ paddingY: 0, px: 2.5, justifyContent: isSideBarOpen ? 'initial' : 'center' }}
            onClick={handleOpenMenu}
          >
            <ListItemIcon
              sx={{ minWidth: 0, justifyContent: 'center', mr: isSideBarOpen ? 3 : 'auto' }}
            >
              <QuestionMark
                sx={{ color: (t) => t.palette.getContrastText(t.palette.primary.main) }}
              />
            </ListItemIcon>
            <ListItemText primary="Help" sx={{ opacity: isSideBarOpen ? 1 : 0 }} />
          </ListItemButton>
        </Tooltip>
      </ListItem>
      <Popper
        anchorEl={anchor}
        open={Boolean(anchor)}
        placement="right"
        sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}
        onMouseEnter={() => setIsRootPaperHovered(true)}
        onMouseLeave={() => setIsRootPaperHovered(false)}
      >
        <ClickAwayListener onClickAway={handleCloseMenu}>
          <Paper elevation={8} onMouseLeave={handleCloseMenu}>
            <MenuList>
              <a
                rel="noopener noreferrer"
                href="Tel:+61280718750"
                style={LinkStyle}
                onClick={handleCloseMenu}
              >
                <MenuItem>Call us? +61 2 8071 8750</MenuItem>
              </a>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="mailto:support@recruitwizard.com"
                style={LinkStyle}
                onClick={handleCloseMenu}
              >
                <MenuItem>Create a Ticket</MenuItem>
              </a>
              <a
                target="_self"
                href="/training-sessions"
                style={LinkStyle}
                onClick={handleCloseMenu}
              >
                <MenuItem>Training Sessions</MenuItem>
              </a>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="/api-docs"
                style={LinkStyle}
                onClick={handleCloseMenu}
              >
                <MenuItem>API Documentation</MenuItem>
              </a>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://rwzd.io/SearchGuide"
                style={LinkStyle}
                onClick={handleCloseMenu}
              >
                <MenuItem>Download Boolean Search Guide</MenuItem>
              </a>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={'https://rwzd.io/BrowserExtension'}
                style={LinkStyle}
                onClick={handleCloseMenu}
              >
                <MenuItem>Download Browser Extension</MenuItem>
              </a>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://rwzd.io/RemoteSupport"
                style={LinkStyle}
                onClick={handleCloseMenu}
              >
                <MenuItem>Download Remote Support Tool</MenuItem>
              </a>
              <MenuItem onClick={handleOpenShortcutsDialog}>Keyboard Shortcuts</MenuItem>
              <MenuItem onClick={handleOpenServerInfoDialog}>Server Info</MenuItem>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://status.recruitwizard.com"
                style={LinkStyle}
                onClick={handleCloseMenu}
              >
                <MenuItem>System Status</MenuItem>
              </a>
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
      <Dialog open={showShortcutsDialog} fullWidth>
        <DialogTitle>Keyboard Shortcuts</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Divider>Global</Divider>
            <div>
              <KeyStyled content="ctrl" ignoreMarginRight /> {' + '} <KeyStyled content="k" />:
              Perform a Lookup
            </div>
            <div>
              <KeyStyled content="ctrl" ignoreMarginRight /> {' + '} <KeyStyled content="i" />: Show
              Server Info
            </div>
            <div>
              <KeyStyled content="]" />: Expand / Collapse Side Bar
            </div>
            <div></div>
            <Divider>Within module record</Divider>
            <div>
              <KeyStyled content="n" />: Open Create Note Dialog
            </div>
            <div>
              <KeyStyled content="e" />: Open Edit Page
            </div>
            <div>
              <KeyStyled content="t" />: Open Tag Admin Dialog
            </div>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="success" onClick={() => setShowShortcutsDialog(false)}>
            Ok
          </Button>
        </DialogActions>
      </Dialog>
      <ServerInfoDialog
        open={showServerInfoDialog}
        closeHandler={() => setShowServerInfoDialog(false)}
      />
    </>
  );
}
